import { ReactNode, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, MenuItem, Select, Stack, TextField } from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandLess,
  ExpandMore,
  MoreVert as MoreVertIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Controller, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { selectDefaultAssessmentScaleGroupId } from 'store/options';
import { MetricFormat, MetricPerformanceFactor } from 'constants/scorecards';
import { usePerformanceFactorOptions } from 'hooks/scorecards';
import { useNumberValidator } from 'hooks/form';
import {
  useErrorClearing,
  useUniqueNameValidation,
} from 'hooks/GoalsAndSubgoalsForm';
import { TooltipInfoIcon, TooltipWithIcon } from 'components/Select';
import metricTexts from 'i18n/metric';
import { formTexts } from 'i18n';
import { GoalField } from 'components/GoalsAndSubgoalsForm/interfaces';
import GoalSettingsDialog from 'pages/Scorecard/EditScorecard/GoalSettingsDialog.component';
import GoalTypeSelect from 'pages/Scorecard/EditScorecard/GoalTypeSelect.component';
import useSubgoalListActions from 'pages/Scorecard/EditScorecard/useSubgoalListActions';
import restrictToVerticalAxis from 'pages/Scorecard/EditScorecard/verticalDnd';
import {
  ChevronButton,
  ContentArea,
  DescriptionFieldGroup,
  FieldGroup,
  FieldLabel,
  FieldsGrid,
  Header,
  HeaderInner,
  HeaderTitle,
  RemoveGoalButton,
  SectionRoot,
  SettingsLink,
  descriptionFieldSx,
} from 'pages/Scorecard/EditScorecard/GoalEditor.styled';
import {
  FormatSelect,
  RemoveButton,
  RowDragHandle,
  SubgoalNameCell,
  Table,
  TableWrapper,
  WeightField,
} from 'pages/Scorecard/EditScorecard/MetricGoalEditor.styled';
import useCommonGoalEditGating from './useCommonGoalEditGating';

interface CommonGoalMetricEditorProps {
  goalIndex: number;
  goalsCount: number;
  onRemove?: () => void;
}

const newMetricSubgoal = (assessmentScaleGroupId: string | number) => ({
  name: '',
  description: '',
  weight: 0,
  assessmentScaleGroupId,
  metricTarget: 0,
  metricFormat: MetricFormat.NUMBER as string,
  metricPerformanceFactor: MetricPerformanceFactor.PRORATED as string,
});

interface SortableMetricRowProps {
  id: string;
  anchorId?: string;
  children: (dragHandle: ReactNode) => ReactNode;
}

function SortableMetricRow({ id, anchorId, children }: SortableMetricRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <tr
      ref={setNodeRef}
      id={anchorId}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        zIndex: isDragging ? 4 : undefined,
        scrollMarginTop: 130,
      }}
    >
      {children(
        <RowDragHandle
          size="small"
          aria-label="reorder subgoal"
          {...attributes}
          {...listeners}
        >
          <MoreVertIcon sx={{ fontSize: 18 }} />
        </RowDragHandle>,
      )}
    </tr>
  );
}

export default function CommonGoalMetricEditor({
  goalIndex,
  goalsCount,
  onRemove,
}: CommonGoalMetricEditorProps) {
  const { control, watch } = useFormContext<GoalField>();
  const { formatMessage } = useIntl();
  const goal = watch(`goals.${goalIndex}`);
  const [expanded, setExpanded] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const performanceFactorOptions = usePerformanceFactorOptions();
  const nameValidationRules = useUniqueNameValidation('goals', goalIndex);
  const metricTargetValidator = useNumberValidator(
    100000000,
    0.000001,
    false,
    true,
  );
  useErrorClearing(goalIndex);
  const { canEdit, canDeleteGoal } = useCommonGoalEditGating(
    goalIndex,
    goalsCount,
  );
  const defaultAssessmentScaleGroupId = useSelector(
    selectDefaultAssessmentScaleGroupId,
  );
  const {
    fields,
    sensors,
    handleAppendSubgoal,
    handleRemoveSubgoal,
    handleReorderSubgoals,
  } = useSubgoalListActions(goalIndex, () =>
    newMetricSubgoal(defaultAssessmentScaleGroupId),
  );

  const formatOptions = [
    { id: MetricFormat.NUMBER, name: formatMessage(metricTexts.number) },
    { id: MetricFormat.CURRENCY, name: formatMessage(metricTexts.currency) },
    {
      id: MetricFormat.PERCENTAGE,
      name: formatMessage(metricTexts.percentage),
    },
  ];

  return (
    <SectionRoot>
      <Header>
        <HeaderInner>
          <ChevronButton
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? 'collapse goal' : 'expand goal'}
          >
            {expanded ? (
              <ExpandLess sx={{ fontSize: 16 }} />
            ) : (
              <ExpandMore sx={{ fontSize: 16 }} />
            )}
          </ChevronButton>
          <HeaderTitle>{goal?.name || `Goal ${goalIndex + 1}`}</HeaderTitle>
          <SettingsLink type="button" onClick={() => setSettingsOpen(true)}>
            <SettingsIcon sx={{ fontSize: 20 }} />
            <FormattedMessage
              id="pages.scorecards.edit.goalSettings"
              defaultMessage="Goal settings"
            />
          </SettingsLink>
          {onRemove && canDeleteGoal && (
            <RemoveGoalButton
              onClick={onRemove}
              size="small"
              aria-label="remove goal"
            >
              <DeleteIcon fontSize="small" />
            </RemoveGoalButton>
          )}
        </HeaderInner>
      </Header>
      {expanded && (
        <ContentArea>
          <FieldsGrid>
            <FieldGroup className="name">
              <FieldLabel htmlFor={`cg-metric-goal-${goalIndex}-name`}>
                <FormattedMessage
                  id="pages.scorecards.edit.goalName"
                  defaultMessage="Goal Name"
                />
                <span className="required">*</span>
              </FieldLabel>
              <Controller
                name={`goals.${goalIndex}.name`}
                control={control}
                rules={{
                  required: formatMessage(formTexts.errorRequired),
                  maxLength: {
                    value: 256,
                    message: formatMessage(formTexts.errorMaxLength, {
                      fieldName: '',
                      maxLength: 256,
                    }),
                  },
                  validate: nameValidationRules,
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    id={`cg-metric-goal-${goalIndex}-name`}
                    fullWidth
                    size="small"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    inputProps={{ maxLength: 256 }}
                    disabled={!canEdit}
                  />
                )}
              />
            </FieldGroup>
            <GoalTypeSelect
              goalIndex={goalIndex}
              disabled={!canEdit}
              className="type"
            />
          </FieldsGrid>
          <DescriptionFieldGroup>
            <FieldLabel htmlFor={`cg-metric-goal-${goalIndex}-description`}>
              <FormattedMessage
                id="pages.scorecards.edit.description"
                defaultMessage="Description"
              />
            </FieldLabel>
            <Controller
              name={`goals.${goalIndex}.description`}
              control={control}
              rules={{
                maxLength: {
                  value: 3000,
                  message: formatMessage(formTexts.errorMaxLength, {
                    fieldName: '',
                    maxLength: 3000,
                  }),
                },
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  id={`cg-metric-goal-${goalIndex}-description`}
                  fullWidth
                  size="small"
                  multiline
                  placeholder={formatMessage({
                    id: 'pages.scorecards.edit.descriptionPlaceholder',
                    defaultMessage: 'Add a description for this goal',
                  })}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  inputProps={{ maxLength: 3000 }}
                  disabled={!canEdit}
                  sx={descriptionFieldSx}
                />
              )}
            />
          </DescriptionFieldGroup>
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <th>
                    <FormattedMessage
                      id="pages.scorecards.edit.subgoalsHeader"
                      defaultMessage="Subgoals"
                    />
                  </th>
                  <th>
                    <FormattedMessage
                      id="pages.scorecards.edit.weightHeader"
                      defaultMessage="Weight"
                    />
                  </th>
                  <th>
                    <FormattedMessage
                      id="pages.scorecards.edit.format"
                      defaultMessage="Format"
                    />
                  </th>
                  <th>
                    <FormattedMessage
                      id="pages.scorecards.view.target"
                      defaultMessage="Target"
                    />
                  </th>
                  <th>
                    <FormattedMessage
                      id="pages.scorecards.edit.performanceFactor02"
                      defaultMessage="Performance factor 0-2"
                    />
                  </th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  modifiers={[restrictToVerticalAxis]}
                  onDragEnd={handleReorderSubgoals}
                >
                  <SortableContext
                    items={fields.map((f) => f.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {fields.map((field, subgoalIndex) => {
                      const path =
                        `goals.${goalIndex}.subgoals.${subgoalIndex}` as const;
                      const subgoalId = goal?.subgoals?.[subgoalIndex]?.id;
                      const anchorId =
                        typeof subgoalId === 'number'
                          ? `subgoal-${subgoalId}`
                          : undefined;
                      const renderCells = (dragHandle?: ReactNode) => (
                        <>
                          <th
                            style={canEdit ? { paddingLeft: 32 } : undefined}
                          >
                            {dragHandle}
                            <SubgoalNameCell>
                              <Controller
                                name={`${path}.name`}
                                control={control}
                                rules={{
                                  required: formatMessage(
                                    formTexts.errorRequired,
                                  ),
                                  maxLength: {
                                    value: 256,
                                    message: formatMessage(
                                      formTexts.errorMaxLength,
                                      {
                                        fieldName: '',
                                        maxLength: 256,
                                      },
                                    ),
                                  },
                                }}
                                render={({ field: f, fieldState }) => (
                                  <TextField
                                    {...f}
                                    fullWidth
                                    size="small"
                                    placeholder={formatMessage({
                                      id: 'pages.scorecards.edit.subgoalNamePlaceholder',
                                      defaultMessage: 'Subgoal name',
                                    })}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    inputProps={{ maxLength: 256 }}
                                    disabled={!canEdit}
                                  />
                                )}
                              />
                              <Controller
                                name={`${path}.description`}
                                control={control}
                                rules={{
                                  maxLength: {
                                    value: 3000,
                                    message: formatMessage(
                                      formTexts.errorMaxLength,
                                      {
                                        fieldName: '',
                                        maxLength: 3000,
                                      },
                                    ),
                                  },
                                }}
                                render={({ field: f, fieldState }) => (
                                  <TextField
                                    {...f}
                                    fullWidth
                                    multiline
                                    size="small"
                                    placeholder={formatMessage({
                                      id: 'pages.scorecards.edit.subgoalDescriptionPlaceholder',
                                      defaultMessage:
                                        'Add a description for this subgoal',
                                    })}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    inputProps={{ maxLength: 3000 }}
                                    disabled={!canEdit}
                                  />
                                )}
                              />
                            </SubgoalNameCell>
                          </th>
                          <td>
                            <Controller
                              name={`${path}.weight`}
                              control={control}
                              rules={{
                                required: formatMessage(formTexts.errorRequired),
                              }}
                              render={({ field: f, fieldState }) => (
                                <WeightField
                                  {...f}
                                  fullWidth
                                  size="small"
                                  type="number"
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message}
                                  onFocus={(e) => e.target.select()}
                                  disabled={!canEdit}
                                />
                              )}
                            />
                          </td>
                          <td>
                            <Controller
                              name={`${path}.metricFormat`}
                              control={control}
                              rules={{
                                required: formatMessage(formTexts.errorRequired),
                              }}
                              render={({ field: f, fieldState }) => (
                                <FormatSelect
                                  {...f}
                                  fullWidth
                                  size="small"
                                  value={f.value ?? MetricFormat.NUMBER}
                                  error={!!fieldState.error}
                                  disabled={!canEdit}
                                >
                                  {formatOptions.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                      {option.name}
                                    </MenuItem>
                                  ))}
                                </FormatSelect>
                              )}
                            />
                          </td>
                          <td>
                            <Controller
                              name={`${path}.metricTarget`}
                              control={control}
                              rules={{ validate: metricTargetValidator }}
                              render={({ field: f, fieldState }) => (
                                <TextField
                                  {...f}
                                  fullWidth
                                  size="small"
                                  type="number"
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message}
                                  disabled={!canEdit}
                                />
                              )}
                            />
                          </td>
                          <td>
                            <Controller
                              name={`${path}.metricPerformanceFactor`}
                              control={control}
                              rules={{
                                required: formatMessage(formTexts.errorRequired),
                              }}
                              render={({ field: f, fieldState }) => (
                                <Select
                                  {...f}
                                  fullWidth
                                  size="small"
                                  error={!!fieldState.error}
                                  value={
                                    f.value ?? MetricPerformanceFactor.PRORATED
                                  }
                                  disabled={!canEdit}
                                >
                                  {performanceFactorOptions.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                      <Stack
                                        direction="row"
                                        alignItems="center"
                                        width="100%"
                                      >
                                        {option.name}
                                        {option.tooltipWithIcon && (
                                          <TooltipWithIcon
                                            title={option.tooltipWithIcon}
                                            disableInteractive
                                          >
                                            <TooltipInfoIcon />
                                          </TooltipWithIcon>
                                        )}
                                      </Stack>
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                            />
                          </td>
                          <td>
                            <RemoveButton
                              onClick={() => handleRemoveSubgoal(subgoalIndex)}
                              size="small"
                              aria-label="remove subgoal"
                              disabled={!canEdit || fields.length <= 1}
                            >
                              <DeleteIcon fontSize="small" />
                            </RemoveButton>
                          </td>
                        </>
                      );
                      return canEdit ? (
                        <SortableMetricRow
                          key={field.id}
                          id={field.id}
                          anchorId={anchorId}
                        >
                          {renderCells}
                        </SortableMetricRow>
                      ) : (
                        <tr
                          key={field.id}
                          id={anchorId}
                          style={{ scrollMarginTop: 130 }}
                        >
                          {renderCells()}
                        </tr>
                      );
                    })}
                  </SortableContext>
                </DndContext>
              </tbody>
            </Table>
          </TableWrapper>
          {canEdit && (
            <div>
              <Button
                variant="outlined"
                size="medium"
                startIcon={<AddIcon />}
                onClick={handleAppendSubgoal}
              >
                <FormattedMessage
                  id="pages.scorecards.edit.addSubgoal"
                  defaultMessage="Add subgoal"
                />
              </Button>
            </div>
          )}
        </ContentArea>
      )}
      {settingsOpen && (
        <GoalSettingsDialog
          goalIndex={goalIndex}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </SectionRoot>
  );
}
